<template>
  <div>
    <el-form
      :rules="rules"
      :model="editableRow"
      ref="rest-list-form"
      @submit.prevent.native
      class="form"
    >
      <rest-list
        :is-editing-row="isEditingRow"
        :edit-row-data="editRowData"
        v-bind="$attrs"
        ref="restList"
        @dataLoaded="cancelEditRows"
        @row-click="onRowClick"
        @selection-change="$emit('selection-change', $event)"
        :after-fetch="afterFetch"
        :watch-payload="watchPayload"
        :top-data="topData"
        :max-height="maxHeight"
        :loading="isSaving"
        :before-page-change="beforePageChange"
      >
        <slot />
      </rest-list>
    </el-form>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestApi from '@/core/services/RestApi'
import _ from 'lodash'
export default {
  name: 'RestListEditable',
  components: { RestList },

  provide() {
    return {
      saveChanges: this.saveChanges,
      cancelChanges: this.cancelChanges,
      editRow: this.editRow,
      isEditingRow: this.isEditingRow,
      editRowData: this.editRowData,
      isUpdatable: this.isUpdatable
    }
  },
  data() {
    return {
      index: null,
      editableData: [],
      isSaving: false,
      lastHiddenElements: []
    }
  },
  props: {
    topData: {
      type: Array,
      required: false
    },
    watchPayload: {
      type: Boolean,
      default: true,
      required: false
    },
    afterFetch: {
      type: Function,
      required: false
    },
    maxHeight: {
      type: Number,
      required: false
    },
    rules: {
      type: Object,
      required: false
    },
    multipleEdit: {
      type: Boolean,
      required: false,
      default: false
    },
    updateEndpoint: {
      type: Function,
      required: false
    },
    addEndpoint: {
      type: [Object, Function],
      required: false
    },
    saveWarning: {
      type: Boolean,
      required: false,
      default: false
    },
    discardWarning: {
      type: Boolean,
      required: false,
      default: true
    },
    additionalPayload: {
      type: Object,
      required: false,
      default: () => {
        return {}
      }
    },
    clearMask: {
      type: Boolean,
      required: false,
      default: false
    },
    clickOutsideToCancel: {
      type: Boolean,
      required: false,
      default: true
    },
    beforeSave: {
      type: Function,
      required: false
    }
  },

  methods: {
    onRowClick(row) {
      if (!this.clickOutsideToCancel || this.multipleEdit) return
      const clickedIndex = this.$refs.restList.data.indexOf(row)
      if (
        clickedIndex !== this.index &&
        Object.keys(this.editableData).length
      ) {
        this.cancelChanges(this.index)
      }
    },
    async beforePageChange() {
      if (!Object.keys(this.editableData).length) return true
      const result = await this.cancelChanges(this.index)
      return result !== false
    },
    async addRow(row = {}) {
      if (!this.multipleEdit && Object.keys(this.editableData).length) {
        if (!(await this.cancelChanges(this.index))) return
      }
      this.index = 0
      this.$refs.restList.data.unshift(row)
      const newObject = {}

      Object.keys(this.editableData).forEach(key => {
        newObject[parseInt(key) + 1] = this.editableData[key]
      })

      this.editableData = newObject
      this.$set(this.editableData, 0, this.additionalPayload)
      this.editRow(0, true, true)
    },
    async editRow(index, newRow = false, skip = false) {
      if (
        !skip &&
        !this.multipleEdit &&
        Object.keys(this.editableData).length
      ) {
        if (!(await this.cancelChanges(this.index))) return
      }
      this.index = index
      this.$set(
        this.editableData,
        index,
        Object.assign(
          { new: newRow, ...this.additionalPayload },
          _.cloneDeep(this.$refs.restList.data[index])
        )
      )
    },
    isEditingRow(index) {
      return !!this.editableData[index]
    },
    editRowData(index) {
      return this.editableData[index]
    },

    isNewRow(index) {
      return this.editableData[index].new === true
    },

    cancelEditRows() {
      this.editableData = []
      this.$emit('dataLoaded', this.$refs.restList.data)
    },

    updateRow(index, updatedData) {
      this.$set(this.$refs.restList.data, index, updatedData)
      this.$set(this.editableData, index, undefined)
    },

    validate() {
      return new Promise((resolve, reject) => {
        this.$refs['rest-list-form'].validate((isValid, obj) => {
          if (isValid) {
            resolve(isValid)
            return
          }
          const err = Object.values(obj)[0]
          if (err.length) {
            this.$message({
              message: err[0].message,
              type: 'error'
            })
          }
          reject(isValid)
        })
      })
    },
    saveChanges(index) {
      this.index = index
      this.validate().then(() => {
        if (this.saveWarning) {
          this.$msgbox({
            title: this.$t('RestList_SaveTitle'),
            message: this.$t('RestList_SavePrompt'),
            showCancelButton: true,
            confirmButtonText: this.$t('RestList_SaveConfirmButton'),
            cancelButtonText: this.$t('RestList_SaveCancelButton'),
            center: true
          }).then(() => {
            return this.saveRow()
          })
        } else {
          return this.saveRow()
        }
      })
    },

    saveEndpoint(index, row) {
      if (this.isNewRow(index)) {
        return typeof this.addEndpoint === 'function'
          ? this.addEndpoint(row)
          : this.addEndpoint
      } else {
        return this.updateEndpoint(row)
      }
    },

    saveRow() {
      if (this.beforeSave) {
        this.beforeSave(this.editableRow)
      }
      this.isSaving = true
      const endpoint = this.saveEndpoint(this.index, this.editableRow)

      RestApi({
        ...endpoint,
        ...{ data: this.editableRow }
      })
        .then(({ data }) => {
          this.isSaving = false
          this.$message({
            message: this.$t('RestList_SaveSuccessMessage'),
            type: 'success'
          })

          if (endpoint.handler) {
            this.updateRow(this.index, endpoint.handler(data))
          } else {
            this.updateRow(this.index, data)
          }

          delete this.editableData[this.index]
          this.$emit('row-updated', this.editableRow, this.index, data)
        })
        .catch(error => {
          this.isSaving = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    async cancelChanges(index, skip = false) {
      this.index = index
      let editable = { ...this.editableData[index] }
      delete editable.new
      if (
        !skip &&
        this.discardWarning &&
        !_.isEqual(editable, this.$refs.restList.data[index])
      ) {
        return this.$msgbox({
          title: this.$t('RestList_DiscardChangesTitle'),
          message: this.$t('RestList_DiscardChangesPrompt'),
          showCancelButton: true,
          confirmButtonText: this.$t('RestList_DiscardChangesConfirmButton'),
          cancelButtonText: this.$t('RestList_DiscardChangesCancelButton'),
          center: true
        })
          .then(() => {
            return this.discardChanges()
          })
          .catch(() => {
            return false
          })
          .finally(() => {
            if (this.clearMask) {
              document.querySelector('.v-modal').remove()
            }
          })
      } else {
        return this.discardChanges()
      }
    },
    discardChanges() {
      if (this.isNewRow(this.index)) {
        const newObject = {}
        Object.keys(this.editableData).forEach(key => {
          if (parseInt(key) > this.index) {
            newObject[parseInt(key) - 1] = this.editableData[key]
          } else if (parseInt(key) < this.index) {
            newObject[key] = this.editableData[key]
          }
        })
        this.editableData = newObject
        this.$refs.restList.data.splice(this.index, 1)
      } else {
        this.$set(this.editableData, this.index, undefined)
        delete this.editableData[this.index]
      }
      this.$emit('row-discarded')
      return true
    }
  },
  computed: {
    editableRow() {
      return this.editableData[this.index]
    },
    isUpdatable() {
      return !!this.updateEndpoint
    }
  },
  watch: {
    editableRow(val) {
      if (val) {
        this.lastHiddenElements = this.$refs.restList.$el.querySelectorAll(
          `table tbody tr:nth-child(${this.index + 1}) .is-hidden`
        )
        this.lastHiddenElements.forEach(el => {
          el.classList.remove('is-hidden')
        })
      } else {
        this.lastHiddenElements.forEach(el => {
          el.classList.add('is-hidden')
        })
        this.lastHiddenElements = []
      }
    }
  }
}
</script>

<style scoped>
>>> td.editable-row-actions {
  z-index: 51;
}
</style>



// WEBPACK FOOTER //
// src/common/components/rest/RestListEditable.vue